import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faExclamationTriangle,
  faCheckCircle,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

import Loader from "../Loader";
import PixLogo from "../../assets/images/icon/pix-logo.png";

import styles from "./styles.module.scss";

type PaymentStatus = "pending" | "paid";

// const PIX_CODE_PLACEHOLDER = "00020126360014BR.GOV.BCB.PIX2555PIX-CODE-PLACEHOLDER-EXEMPLO-NA-INTEGRACAO520400005303986540510.005802BR5925Prefeitura de Fortaleza6009Fortaleza62070503***6304ABCD";
// const EXPIRATION_PLACEHOLDER = "10h37";

interface PixLocationState {
  copyPast?: string;
  qrCode?: string;
}

const steps = [
  {
    number: "1",
    text: "Abra o app ou banco de sua preferência. Escolha a opção pagar com código Pix \"copia e cola\", ou código QR.",
  },
  {
    number: "2",
    text: "Copie e cole o código, ou escaneie o código QR com a câmera do seu celular. Confira todas as informações e autorize o pagamento.",
  },
  {
    number: "3",
    text: "Você vai receber a confirmação de pagamento no seu e-mail e através dos nossos canais. E pronto!",
  },
];

export default function PagamentoPixContent() {
  const feedbackTimeoutRef = useRef<number>();
  const [copied, setCopied] = useState(false);
  const location = useLocation<PixLocationState>();
  const copyPastFromState = location.state?.copyPast;
  const qrCodeFromState = location.state?.qrCode;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const copyPastFromQuery = searchParams.get("copy_past") || undefined;
  const qrCodeFromQuery = searchParams.get("qr_code") || undefined;

  const paymentStatus: PaymentStatus = "pending";
  const isPending = paymentStatus === "pending";

  const pixCode = useMemo(
    () => copyPastFromState || copyPastFromQuery,
    [copyPastFromQuery, copyPastFromState],
  );
  const qrCode = useMemo(
    () => qrCodeFromState || qrCodeFromQuery || undefined,
    [qrCodeFromQuery, qrCodeFromState],
  );
  // const expirationTime = useMemo(() => EXPIRATION_PLACEHOLDER, []);

  const clearFeedback = useCallback(() => {
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = undefined;
    }
    setCopied(false);
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!pixCode) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(pixCode);
      } else {
        throw new Error("Clipboard API indisponível");
      }
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = pixCode;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }

    clearFeedback();
    setCopied(true);
    feedbackTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      feedbackTimeoutRef.current = undefined;
    }, 3000);
  }, [clearFeedback, pixCode]);

  useEffect(() => () => clearFeedback(), [clearFeedback]);

  return (
    <div className={styles.container}>
      <Link to="/servicos" className={styles.backLink}>
        <FontAwesomeIcon icon={faArrowLeft} className={styles.backLinkIcon} />
        <span>Ver todos os serviços</span>
      </Link>

      <span className={styles.intro}>
        Para efetuar o pagamento, utilize a opção de Pix.
      </span>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <img src={PixLogo} alt="Pix" className={styles.panelLogo} />
          <span className={styles.panelTitle}>Pagamento via Pix</span>
        </div>

        <div className={styles.steps}>
          {steps.map((step) => (
            <div className={styles.step} key={step.number}>
              <span className={styles.stepNumber}>{step.number}</span>
              <p className={styles.stepText}>{step.text}</p>
            </div>
          ))}
        </div>

        {isPending ? (
          <div className={styles.status}>
            <span className={styles.statusDescription}>
              Escaneie o QR Code ou copie o código abaixo, cole em seu banco.
            </span>

            <div className={styles.codeCard} aria-label="QR Code Pix">
              {qrCode ? (
                <img src={qrCode} alt="QR Code Pix" className={styles.codeImage} />
              ) : (
                <Loader />
              )}
            </div>

            <button
              type="button"
              className={`${styles.copyButton} ${copied ? styles.copyButtonSuccess : ""}`}
              onClick={copyToClipboard}
            >
              {copied && (
                <FontAwesomeIcon icon={faCheck} className={styles.copyButtonIcon} />
              )}
              {copied ? "CÓDIGO COPIADO" : "COPIAR CÓDIGO PIX"}
            </button>
            {/* <div className={styles.expiration}>
              <span className={styles.expirationText}>O código é válido até às {expirationTime}</span>
              <span className={styles.expirationLoader}>
                <Loader />
              </span>
            </div> */}

            <p className={styles.statusNote}>
              Se o pagamento não foi confirmado, não se preocupe. Sua solicitação de pagamento será cancelada automaticamente.
            </p>
            <button
              type="button"
              className={`btn btn-primary ${styles.actionButton}`}
              onClick={() => window.close()}
            >
              Concluir e verificar o status
            </button>
          </div>
        ) : (
          <div className={styles.success}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
            <div className={styles.successActions}>
              <button type="button" className={styles.successButtonOutline}>
                Imprimir comprovante
              </button>
              <button type="button" className={styles.successButtonSolid}>
                Concluir
              </button>
            </div>
            <p className={styles.successDescription}>
              Dúvidas sobre o pagamento, comprovante, produto ou serviço podem ser esclarecidas junto à instituição responsável.
            </p>
          </div>
        )}

        {/* {isPending ? (
          <div className={`${styles.banner} ${styles.bannerPending}`}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={styles.bannerIcon} />
            <span className={styles.bannerText}>Aguardando o pagamento.</span>
          </div>
        ) : (
          <div className={`${styles.banner} ${styles.bannerSuccess}`}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.bannerIcon} />
            <span className={styles.bannerText}>Pagamento realizado.</span>
          </div>
        )} */}
      </div>
    </div>
  );
}
